/**
 * Stable error codes shared by the API and the web client.
 * The API returns a code; the client shows a plain-language, localized text
 * with a suggested action and keeps technical details folded (D-39).
 */
export const ERROR_CODES = [
  'validation_failed',
  'unauthenticated',
  'forbidden',
  'not_found',
  'conflict',
  'csrf_failed',
  'rate_limited',
  'invalid_credentials',
  'directory_unavailable',
  'account_deactivated',
  'account_relink_required',
  'membership_from_directory',
  'email_not_verified',
  'token_invalid',
  'password_too_short',
  'password_too_long',
  'password_breached',
  'invite_email_mismatch',
  'already_member',
  'last_owner',
  'role_not_allowed',
  'project_path_invalid',
  'project_path_nested',
  'project_path_taken',
  'workspace_kind_mismatch',
  'payload_too_large',
  'provider_url_invalid',
  'provider_url_blocked',
  'api_key_required',
  'personal_providers_disabled',
  'model_unavailable',
  'service_unavailable',
  'sandbox_unavailable',
  'sandbox_limit',
  'sandbox_error',
  'path_outside_workspace',
  'path_not_found',
  'read_only',
  'is_directory',
  'not_a_directory',
  'sensitive_path',
  'disk_quota_exceeded',
  'not_a_git_repo',
  'git_error',
  'port_not_listening',
  'preview_unreachable',
  'preview_unavailable',
  'snapshot_not_found',
  'snapshot_failed',
  'no_model_configured',
  'context_overflow',
  'quota_exceeded',
  // Phase 13f (D-124, D-125): the license is read-only; a product the license does not have
  'license_read_only',
  'product_not_licensed',
  // Phase 15c (D-165): an app older than the installation's minimum; a bearer request without a known app
  'client_update_required',
  'client_unknown',
  'provider_unavailable',
  'no_visible_reply',
  'run_interrupted',
  'queue_full',
  'run_not_active',
  'approval_not_pending',
  'mode_not_allowed',
  'turn_active',
  'rewind_before_compaction',
  'nothing_to_compact',
  'compaction_failed',
  'nothing_to_regenerate',
  'memory_disabled',
  'mcp_test_failed',
  'mcp_needs_auth',
  'mcp_not_approved',
  'mcp_unavailable',
  'mcp_user_servers_disabled',
  // Phase 15e: Microsoft 365, the built-in connection
  'microsoft_not_configured',
  'microsoft_not_available',
  'microsoft_not_connected',
  'microsoft_reconnect',
  'microsoft_consent_required',
  'microsoft_unavailable',
  'microsoft_secret_required',
  // Microsoft 365 part 3 (D-220…): the feature's scopes are not granted yet ("Yeni izin gerekiyor · yeniden bağlan");
  // a write card is decided on its own route only; the card is no longer the one shown (another hash, decided,
  // expired); a recipient outside the pilot list (MS365_PILOT_RECIPIENTS); a shared place without the warning's "Anladım"
  'microsoft_scopes_required',
  'microsoft_card_required',
  'microsoft_card_changed',
  'recipient_not_allowed',
  'acknowledgement_required',
  'name_taken',
  'skill_invalid',
  'skill_source_unavailable',
  'skill_external_disabled',
  'skill_choose_folder',
  'workspace_untrusted',
  'search_unavailable',
  'unsupported_document',
  'not_ready',
  'model_not_allowed',
  'organization_limit',
  // Phase 16a (ADR-0023): the sign-up's code; the person's plan (file size, documents, own connections); a scope this
  // edition does not have (Herkes, Ekip in bireysel)
  'code_invalid',
  'code_expired',
  'registration_unknown',
  'file_too_large',
  'document_limit',
  'plan_required',
  'scope_not_available',
  // Phase 16a (D-271): a model call waited too long in the bireysel installation's queue (no words of its own: the
  // generic failure line, so nothing tells of load)
  'model_busy',
  // Phase 16c: Pro cannot be bought here yet (no provider); a live subscription already; the legal texts changed
  // since the page was opened
  'payments_closed',
  'already_subscribed',
  'legal_version_changed',
  // Phase 16c.1: own skills or connections at the package's limit; a model a package or the Sohbet task holds; one
  // that subscribers keep until their announced change; a provider with a model in a package or kept so
  'skill_limit',
  'connection_limit',
  'model_in_plan',
  'model_in_task',
  'model_kept',
  'provider_in_plan',
  'internal_error',
  'network_error',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

export const ERROR_HTTP_STATUS: Record<ErrorCode, number> = {
  validation_failed: 400,
  unauthenticated: 401,
  forbidden: 403,
  not_found: 404,
  conflict: 409,
  csrf_failed: 403,
  rate_limited: 429,
  invalid_credentials: 401,
  directory_unavailable: 503,
  account_deactivated: 403,
  // Phase 13d-2: a rebuilt directory's new account waits for an admin's "Hesabı bağla"
  account_relink_required: 403,
  // Phase 13d-2: in the kurumsal edition people and teams come from the directory (access is turned off instead)
  membership_from_directory: 409,
  email_not_verified: 403,
  token_invalid: 400,
  password_too_short: 400,
  password_too_long: 400,
  password_breached: 400,
  invite_email_mismatch: 403,
  already_member: 409,
  last_owner: 409,
  role_not_allowed: 403,
  project_path_invalid: 400,
  project_path_nested: 409,
  project_path_taken: 409,
  workspace_kind_mismatch: 400,
  payload_too_large: 413,
  provider_url_invalid: 400,
  provider_url_blocked: 400,
  api_key_required: 400,
  personal_providers_disabled: 403,
  model_unavailable: 409,
  service_unavailable: 503,
  sandbox_unavailable: 503,
  sandbox_limit: 429,
  sandbox_error: 422,
  path_outside_workspace: 400,
  path_not_found: 404,
  read_only: 400,
  is_directory: 400,
  not_a_directory: 400,
  sensitive_path: 403,
  disk_quota_exceeded: 507,
  not_a_git_repo: 409,
  git_error: 422,
  port_not_listening: 409,
  preview_unreachable: 502,
  // v0.15a.3: previews are off on this installation (an IP address and no preview domain)
  preview_unavailable: 409,
  snapshot_not_found: 404,
  snapshot_failed: 500,
  no_model_configured: 409,
  context_overflow: 409,
  quota_exceeded: 429,
  license_read_only: 403,
  product_not_licensed: 403,
  client_update_required: 426,
  client_unknown: 400,
  provider_unavailable: 503,
  no_visible_reply: 502,
  run_interrupted: 503,
  queue_full: 429,
  run_not_active: 409,
  approval_not_pending: 409,
  turn_active: 409,
  rewind_before_compaction: 409,
  nothing_to_compact: 409,
  compaction_failed: 502,
  nothing_to_regenerate: 409,
  memory_disabled: 403,
  mcp_test_failed: 422,
  mcp_needs_auth: 409,
  mcp_not_approved: 409,
  mcp_unavailable: 502,
  mcp_user_servers_disabled: 403,
  microsoft_not_configured: 409,
  microsoft_not_available: 403,
  microsoft_not_connected: 409,
  microsoft_reconnect: 409,
  microsoft_consent_required: 409,
  microsoft_unavailable: 503,
  microsoft_secret_required: 400,
  microsoft_scopes_required: 409,
  microsoft_card_required: 409,
  microsoft_card_changed: 409,
  recipient_not_allowed: 403,
  acknowledgement_required: 409,
  name_taken: 409,
  skill_invalid: 422,
  skill_source_unavailable: 502,
  skill_external_disabled: 403,
  skill_choose_folder: 409,
  workspace_untrusted: 403,
  search_unavailable: 503,
  unsupported_document: 415,
  not_ready: 409,
  model_not_allowed: 403,
  organization_limit: 409,
  code_invalid: 400,
  code_expired: 400,
  registration_unknown: 400,
  file_too_large: 413,
  document_limit: 409,
  plan_required: 403,
  scope_not_available: 400,
  model_busy: 503,
  payments_closed: 409,
  skill_limit: 409,
  connection_limit: 409,
  model_in_plan: 409,
  model_in_task: 409,
  model_kept: 409,
  provider_in_plan: 409,
  already_subscribed: 409,
  legal_version_changed: 409,
  mode_not_allowed: 403,
  internal_error: 500,
  network_error: 0,
};

/** SA-023: a used-up token budget: which one, when it resets, and (when known) what was used of how much. */
export interface QuotaNoticeDto {
  scope: 'user_daily' | 'tenant_monthly';
  resetsAt: string;
  used?: number;
  limit?: number;
}

/** Phase 16a: a limit of the person's plan was reached: which one, and the plan's value (MB or a count) */
export interface PlanLimitNoticeDto {
  kind: 'file_size' | 'documents' | 'connections' | 'skills';
  plan: 'free' | 'pro';
  limit: number;
  /** documents, skills, connections: how many are kept now; file_size: the file's size in MB (one decimal) */
  used?: number;
}

export interface ApiErrorBody {
  error: {
    code: ErrorCode;
    /** Technical message in English; the UI shows it only under "details". */
    message: string;
    requestId?: string;
    /** Field-level validation issues (`path` is dot-joined). */
    fields?: { path: string; code: string }[];
    /** Seconds until a rate-limited action may be retried. */
    retryAfter?: number;
    /** SA-023: which token budget is used up and when it resets */
    quota?: QuotaNoticeDto;
    /** Phase 16a: which limit of the plan */
    limit?: PlanLimitNoticeDto;
    /** Phase 16a: a wrong sign-up code: how many tries are left */
    attemptsLeft?: number;
  };
}

export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && (ERROR_CODES as readonly string[]).includes(value);
}
